/**
 * Every table on `/studio`, as cells that carry **both** what the page prints and what
 * a spreadsheet computes on.
 *
 * There is one reason this is a module and not table literals inside the sections:
 * the **Export button**. `.docs/20` §A asks for "CSV of the current period's tables,
 * built client-side from the read-model data already on the page", and a CSV
 * assembled separately from the tables it claims to be is a CSV that can disagree
 * with the screen.
 *
 * **A cell is a pair, since session 24 (R2-28).** Rows used to be `string[]` — the
 * rendered string, and only that — so the export was a picture of the page rather than
 * its data: `"$3,230"`, `67.8%`, `—` for zero, and `−$890` with a real minus sign
 * (U+2212, `money.ts` `formatWithMinusSign`). In Excel every column was text, nothing
 * summed, and `−$890` did not parse at all. An export exists to be computed on.
 *
 * So `Cell` is `{ text, value }`: `text` is what `ChartTable` renders and is unchanged
 * to the character, `value` is the figure underneath it, and `DataColumn.unit` names
 * what that figure is in so the CSV header can say `MRR at month end (USD)`. The two
 * are produced in the same expression from the same source, which is the property the
 * old parallel-array alternative could not have.
 *
 * Three consequences worth stating, because each is a fact the old export lost:
 *
 *   - **A zero and a nothing stop being the same cell.** `—` was printed for a
 *     movement of zero *and* for a client with no ratio at all. A zero now exports
 *     `0.00` and a nothing exports an empty cell.
 *   - **Money is major units to two decimals** — `4291.40`, not `429140` and not
 *     `"$4,291.40"`. `money.ts` `toMajorUnits` is the conversion, in the one file
 *     allowed to do arithmetic on money.
 *   - **A share is the fraction the read model owns**, not the rounded percentage the
 *     page prints. `schemas/readmodels/analytics.ts`: "every `share` and `ratio` is a
 *     fraction, not basis points." The column header says `(fraction of 1)`; the
 *     screen still reads `67.8%`.
 *
 * Six of the nine tables are used twice — as a chart's `details.tableview`
 * (CONTRACT.md §2.13's non-negotiable fallback, through `onScreen` below) and as a
 * section of the export. Three are not, and the difference is worth stating rather
 * than letting the docstring overclaim:
 *
 *   - `clientHealthTable` has **no** counterpart on the page. §3 draws meters, and
 *     the ratio behind each meter is the number a re-scope conversation needs.
 *   - `packageMixTable` and `pipelineTable` are the export's own rendering of two
 *     things the page draws as markup rather than as a chart — the package mix is a
 *     real `Table` with `Tr variant="total"` and a list of `ClientLink`s, and the
 *     pipeline is a list of `Row`s. Neither can be a `Cell[][]`, so those two
 *     sections of the CSV are a second rendering and are the one place this file's
 *     guarantee does not hold. It is also why those three may carry a column the
 *     screen does not have, which `pipelineTable`'s amount basis is.
 *
 * **`TableView` has no row variants**, so the six-month subtotal renders as an
 * ordinary row where `analytics.html` writes `tr.is-total`, and an under-floor client
 * is marked by its own label rather than by `tr.is-need`. `Table`/`Tr` support both
 * (`components/portal/Table.tsx`); the chart's fallback does not, and the report asks
 * for it.
 *
 * Nothing here computes a figure. The one exception is that six-month subtotal, and
 * DESIGN.md §Non-negotiable names it: "a subtotal of rows that are visible on the
 * same page … must reconcile exactly, and its components must remain reachable". It
 * is added with `packages/shared`'s `sum`, the only place money is added.
 */

import { formatList, sum, toMajorUnits, type Currency, type Money } from '@fareast/shared'

import type { ChartColumn, ChartTable } from '../../../components/portal/Chart.js'
import type { Formatter } from '../../../format/useFormat.js'
import { currencyOf } from './axis.js'
import type {
  HealthData,
  MrrData,
  PackageMixData,
  PipelineData,
  RateData,
  RevenueData,
  TierMixData,
} from './data.js'
import { MODEL_LABEL, SHAPE_LABEL } from './labels.js'
import { WHOLE_AMOUNT } from './render.js'
import { SHARE_DIGITS, WHOLE_DIGITS, percentText } from '../../../format/percent.js'

/** A component that is zero prints as an em dash; a reading of zero prints as money. */
const NOTHING = '—'

/** How many decimals a money `value` carries. Both currencies here are two. */
const MONEY_DECIMALS = 2

/**
 * One cell: what the page prints, and what the export computes on.
 *
 * They are different strings on purpose and the difference is never a rounding — the
 * `value` is always the fuller of the two, because a reader can round a number and
 * cannot un-round one.
 */
export type Cell = {
  /** The rendered string. `$3,230`, `67.8%`, `—`. What `ChartTable` shows. */
  readonly text: string
  /** The figure underneath it. `3230.00`, `0.6777234`, `''` for nothing at all. */
  readonly value: string
}

/**
 * What a column's `value` is in, for the CSV header — `MRR at month end (USD)`.
 *
 * A closed list rather than free text, so a new column has to choose one of the units
 * a reader's spreadsheet already knows how to treat, and so `Currency` stays the
 * source of the two money ones.
 */
export type CellUnit = Currency | 'fraction of 1' | 'count' | 'minutes' | 'YYYY-MM' | 'ISO date'

/** A `ChartColumn` plus the unit its figures are in. The unit is export-only. */
export type DataColumn = ChartColumn & { readonly unit?: CellUnit }

export type DataTable = {
  /** What the table is, for the export. The `details` summary stays "Table view". */
  readonly title: string
  readonly columns: readonly DataColumn[]
  readonly rows: readonly (readonly Cell[])[]
}

/**
 * The same table as `Chart`'s fallback reads it: the rendered halves, unchanged.
 *
 * `ChartTable.rows` is `ReactNode[][]` and a string is a `ReactNode`, so this is a
 * projection and not a second rendering — every character the page shows is still
 * decided in the builder below it.
 */
export function onScreen(table: DataTable): ChartTable {
  return {
    columns: table.columns,
    rows: table.rows.map((row) => row.map((cell) => cell.text)),
  }
}

/* ------------------------------------------------------------------- the cells */

/** A cell that is a word. The page and the export say the same thing. */
function words(text: string): Cell {
  return { text, value: text }
}

/** A word on screen, a different one in the export. Two labels for one fact. */
function labelled(text: string, value: string): Cell {
  return { text, value }
}

/** Nothing at all: `—` on screen, an empty cell in the export. Never `0`. */
const MISSING: Cell = { text: NOTHING, value: '' }

/** Money. `$3,230` on screen, `3230.00` in the export — which sums. */
function amount(format: Formatter, value: Money): Cell {
  return {
    text: format.money(value, WHOLE_AMOUNT),
    value: toMajorUnits(value).toFixed(MONEY_DECIMALS),
  }
}

/** Money that may be absent. `—` and an empty cell, never `0.00`. */
function amountOrMissing(format: Formatter, value: Money | null): Cell {
  return value === null ? MISSING : amount(format, value)
}

/**
 * A movement component. `—` on screen when it is zero, `0.00` in the export.
 *
 * The old export printed `—` for both this and a null ratio, so a month in which
 * nothing churned and a client with no ratio to compute were the same cell.
 */
function movement(format: Formatter, value: Money): Cell {
  const cell = amount(format, value)
  return value.amount === 0 ? { text: NOTHING, value: cell.value } : cell
}

/** A share or a ratio: rounded on screen, the read model's own fraction in the export. */
function fraction(format: Formatter, value: number | null, digits: number): Cell {
  return value === null
    ? MISSING
    : { text: percentText(format, value, digits), value: String(value) }
}

/** A count of things. `1,180` on screen, `1180` in the export. */
function count(format: Formatter, value: number): Cell {
  return { text: format.number(value), value: String(value) }
}

/** A month: `Jan 2026` on screen, `2026-01` in the export — which sorts. */
function month(format: Formatter, period: string): Cell {
  return { text: format.month(period), value: period }
}

/** A day: `19 Aug 2026` on screen, `2026-08-19` in the export. */
function day(format: Formatter, instant: string | null, absent: string): Cell {
  return instant === null
    ? { text: absent, value: '' }
    : { text: format.date(instant), value: instant.slice(0, 'YYYY-MM-DD'.length) }
}

/* --------------------------------------------------------------------- MRR */

export function mrrSeriesTable(mrr: MrrData, format: Formatter): DataTable {
  const currency = currencyOf(mrr.series.map((point) => point.mrr))

  return {
    title: 'MRR at month end',
    columns: [
      { label: 'Month', unit: 'YYYY-MM' },
      { label: 'MRR at month end', right: true, unit: currency },
    ],
    rows: mrr.series.map((point) => [month(format, point.month), amount(format, point.mrr)]),
  }
}

export function mrrMovementsTable(mrr: MrrData, format: Formatter): DataTable {
  const currency = currencyOf(mrr.movements.map((one) => one.net))

  return {
    title: 'MRR movements',
    columns: [
      { label: 'Month', unit: 'YYYY-MM' },
      { label: 'New', right: true, unit: currency },
      { label: 'Expansion', right: true, unit: currency },
      { label: 'Contraction', right: true, unit: currency },
      { label: 'Churn', right: true, unit: currency },
      { label: 'Net', right: true, unit: currency },
    ],
    rows: mrr.movements.map((one) => [
      month(format, one.month),
      movement(format, one.new),
      movement(format, one.expansion),
      movement(format, one.contraction),
      movement(format, one.churn),
      amount(format, one.net),
    ]),
  }
}

/* ------------------------------------------------------------ revenue by model */

export function revenueShareTable(
  revenue: RevenueData,
  heading: string,
  format: Formatter,
): DataTable {
  return {
    title: `Revenue by billing model — ${heading}`,
    columns: [
      { label: 'Model' },
      { label: `Share of ${heading}`, right: true, unit: 'fraction of 1' },
    ],
    rows: revenue.current.shares.map((one) => [
      words(MODEL_LABEL[one.model]),
      fraction(format, one.share, SHARE_DIGITS),
    ]),
  }
}

export function revenueMonthsTable(revenue: RevenueData, format: Formatter): DataTable {
  const months = revenue.months
  const currency = currencyOf(months.map((one) => one.total))

  return {
    title: 'Recognised revenue by month',
    columns: [
      { label: 'Month', unit: 'YYYY-MM' },
      { label: 'Retainer', right: true, unit: currency },
      { label: 'Project', right: true, unit: currency },
      { label: 'Extra', right: true, unit: currency },
      { label: 'Usage', right: true, unit: currency },
      { label: 'Total', right: true, unit: currency },
    ],
    rows: [
      ...months.map((one) => [
        month(format, one.month),
        amount(format, one.retainer),
        amount(format, one.project),
        amount(format, one.extra),
        amount(format, one.usage),
        amount(format, one.total),
      ]),
      ...(months.length === 0 ? [] : [revenueTotalRow(months, currency, format)]),
    ],
  }
}

type RevenueMonth = RevenueData['months'][number]

/**
 * The closing row — the five columns above it, added through `sum`.
 *
 * Its month cell reads `6 months` on screen and exports the word `Total`, so a
 * spreadsheet sorting or filtering the month column does not have to know that one of
 * its values is a sentence.
 */
function revenueTotalRow(
  months: readonly RevenueMonth[],
  currency: Currency,
  format: Formatter,
): readonly Cell[] {
  const column = (pick: (one: RevenueMonth) => RevenueMonth['total']) =>
    amount(format, sum(months.map(pick), currency))

  return [
    labelled(`${format.number(months.length)} months`, 'Total'),
    column((one) => one.retainer),
    column((one) => one.project),
    column((one) => one.extra),
    column((one) => one.usage),
    column((one) => one.total),
  ]
}

/* ------------------------------------------------------------ the guardrails */

/** Client health. Not a table on the page — §3 draws meters — but the export has it. */
export function clientHealthTable(health: HealthData, format: Formatter): DataTable {
  const currency = currencyOf(health.clients.map((one) => one.paid))

  return {
    title: `Client health — ${format.monthLong(health.period)}`,
    columns: [
      { label: 'Client' },
      { label: 'Value delivered', right: true, unit: currency },
      { label: 'Billed', right: true, unit: currency },
      { label: 'Ratio', right: true, unit: 'fraction of 1' },
      { label: 'Months over', right: true, unit: 'count' },
      { label: 'State' },
      { label: 'Action' },
    ],
    rows: health.clients.map((one) => [
      words(one.name),
      amount(format, one.value),
      amount(format, one.paid),
      fraction(format, one.ratio, WHOLE_DIGITS),
      count(format, one.months_over),
      words(one.under_scoped ? 'under-scoped' : one.watch ? 'watch' : 'inside tolerance'),
      words(one.action ?? ''),
    ]),
  }
}

/**
 * The effective rate, in the order the chart draws it.
 *
 * `paid` is a column because it is the numerator `formulae.md` §Effective $/hour
 * names — the retainer line for the month plus the extras agreed in it — and without
 * it the table states a rate whose working nobody can check. The column is headed
 * "Billed this month" rather than "Recognised": recognised revenue is a different
 * figure over a different window (the run bills extras in arrears), and the two names
 * were the whole of how this page and `/studio/clients` came to print two rates.
 *
 * The client cell keeps its "— under the floor" suffix in **both** halves. It is a
 * word, not a figure, and this table is on the page: dropping it from the export
 * would lose a fact the CSV carries nowhere else.
 */
export function effectiveRateTable(
  rate: RateData,
  order: readonly string[],
  format: Formatter,
): DataTable {
  const byClient = new Map(rate.clients.map((one) => [one.client, one]))
  const rows = [
    ...order.flatMap((slug) => {
      const one = byClient.get(slug)
      return one === undefined ? [] : [one]
    }),
    ...rate.clients.filter((one) => !order.includes(one.client)),
  ]
  const currency = currencyOf(rows.map((one) => one.paid))

  return {
    title: `Effective $/hour — ${format.monthLong(rate.period)}`,
    columns: [
      { label: 'Client' },
      { label: '$ / hour', right: true, unit: currency },
      { label: 'Billed this month', right: true, unit: currency },
      { label: 'Retainer minutes', right: true, unit: 'minutes' },
    ],
    rows: rows.map((one) => [
      words(one.under_floor ? `${one.name} — under the floor` : one.name),
      amountOrMissing(format, one.rate),
      amount(format, one.paid),
      count(format, one.minutes),
    ]),
  }
}

/* ------------------------------------------------------------------- the mix */

export function tierMixTable(tiers: TierMixData, format: Formatter): DataTable {
  const currency = currencyOf(tiers.tiers.map((one) => one.mrr))

  return {
    title: 'MRR by market tier',
    columns: [
      { label: 'Tier', unit: 'count' },
      { label: 'MRR', right: true, unit: currency },
      { label: 'Share', right: true, unit: 'fraction of 1' },
    ],
    rows: tiers.tiers.map((one) => [
      labelled(`Tier ${format.number(one.tier)}`, String(one.tier)),
      amount(format, one.mrr),
      fraction(format, one.share, SHARE_DIGITS),
    ]),
  }
}

export function packageMixTable(mix: PackageMixData, format: Formatter): DataTable {
  const currency = currencyOf([mix.total.mrr])

  return {
    title: 'Package mix',
    columns: [
      { label: 'Shape' },
      { label: 'Clients', right: true, unit: 'count' },
      { label: 'MRR', right: true, unit: currency },
      { label: 'Blocks', right: true, unit: 'count' },
    ],
    rows: [
      ...mix.rows.map((row) => [
        words(`${SHAPE_LABEL[row.shape]} — ${formatList(row.clients.map((one) => one.name))}`),
        count(format, row.clients.length),
        amount(format, row.mrr),
        count(format, row.blocks),
      ]),
      [
        words('All'),
        count(format, mix.total.clients),
        amount(format, mix.total.mrr),
        count(format, mix.total.blocks),
      ],
    ],
  }
}

/**
 * The pipeline, with the **basis** of its amount as its own column.
 *
 * `AnalyticsPipelineSchema`: "a one-time total and a package change's $/mo delta are
 * different units and are never summed into one figure." On screen that is carried by
 * the `/mo` suffix `labels.ts` `pipelineAmountLabel` appends; in a spreadsheet a
 * suffix is what makes the column text, so it becomes a column of its own — and a
 * reader who sums the amounts without filtering on it has been told not to.
 *
 * Export-only, so the extra column costs the page nothing.
 */
export function pipelineTable(pipeline: PipelineData, format: Formatter): DataTable {
  const currency = currencyOf(
    pipeline.items.flatMap((item) => (item.amount === null ? [] : [item.amount])),
  )

  return {
    title: 'Pipeline',
    columns: [
      { label: 'Ref' },
      { label: 'Client' },
      { label: 'Kind' },
      { label: 'Title' },
      { label: 'Proposed', unit: 'ISO date' },
      { label: 'Amount', right: true, unit: currency },
      { label: 'Amount basis' },
    ],
    rows: pipeline.items.map((item) => [
      words(item.ref),
      words(item.name),
      words(item.kind === 'project' ? 'quote' : 'package change'),
      words(item.title),
      day(format, item.proposed_at, 'not sent'),
      amountOrMissing(format, item.amount),
      words(item.amount === null ? 'not priced' : item.kind === 'project' ? 'one-time' : 'monthly'),
    ]),
  }
}
